import { Alert } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DateRangeValue } from 'src/components/DateRange/date-range';
import { DynamicForm, FormField } from 'src/components/Forms';
import { getDynamicFormDefaultValues } from 'src/components/Forms/DynamicForm/dynamic-form-default-values';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  useCreatePartnerHoliday,
  useSelectedPartner,
  useUpdatePartnerHoliday,
} from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { isAfterCutoff } from './is-after-cutoff';

type FormValues = {
  title: string;
  dateRange: DateRangeValue;
};

const formFields: FormField[] = [
  {
    type: 'textfield',
    name: 'title',
    'data-testid': 'schedule-holiday-title',
    label: 'Holiday Name',
    defaultValue: '',
    required: true,
  },
  {
    type: 'daterange',
    name: 'dateRange',
    'data-testid': 'schedule-holiday-date-range',
    validate: ({ from, to }) => {
      if (to.isBefore(from)) {
        return 'End date must be after start date';
      }
      return true;
    },
  },
];

export const ScheduleHolidayModal = () => {
  const { closeModal } = useModalMethods(Modals.ScheduleHolidays);
  const { isOpened, modalProps } = useModalState(Modals.ScheduleHolidays);
  const { createPartnerHoliday } = useCreatePartnerHoliday();
  const { updatePartnerHoliday } = useUpdatePartnerHoliday();
  const today = useMemo(dayjs, []);

  const { selectedPartner } = useSelectedPartner();
  const partnerTimezone = selectedPartner?.timeZone;

  let defaultValues: FormValues = {
    title: '',
    dateRange: { from: today.startOf('d'), to: today.endOf('d') },
  };

  if (modalProps?.updateExisting) {
    const { holidayName, holidayStart, holidayEnd } = modalProps.value;
    defaultValues = {
      title: holidayName,
      dateRange: { from: holidayStart, to: holidayEnd },
    };
  }

  const formMethods = useForm<FormValues>({
    defaultValues: getDynamicFormDefaultValues(formFields, defaultValues),
    mode: 'onChange',
  });
  const { formState, handleSubmit } = formMethods;

  const onSubmit = useCallback(
    async (data: FormValues) => {
      closeModal();
      const baseHolidayData = {
        holidayName: data.title,
        holidayStart: data.dateRange.from,
        holidayEnd: data.dateRange.to,
      };
      if (modalProps?.updateExisting) {
        const { id, ...rest } = modalProps.value;
        await updatePartnerHoliday(id, {
          ...rest,
          ...baseHolidayData,
        });
      } else {
        await createPartnerHoliday({
          ...baseHolidayData,
        });
      }
    },
    [
      closeModal,
      createPartnerHoliday,
      modalProps?.updateExisting,
      modalProps?.value,
      updatePartnerHoliday,
    ],
  );

  const showWarning = partnerTimezone ? isAfterCutoff(partnerTimezone) : false;
  const isUpdate = !!modalProps?.updateExisting;

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title={isUpdate ? 'Update Holiday' : 'Add New Holiday'}
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'schedule-holiday-cancel' },
        {
          label: isUpdate ? 'Save holiday' : 'Add holiday',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !formState.isValid,
          testId: 'schedule-holiday-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="schedule-holiday-modal"
    >
      <DynamicForm fields={formFields} formMethods={formMethods} sx={{ gap: 2 }} />
      {showWarning ? (
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="schedule-holiday-cutoff-warning">
          <strong>Caution: Short-Notice Closure</strong>
          <br />
          Conversations escalated before this holiday was added may have already had
          expectations set. Please ensure appropriate communication with affected clients.
        </Alert>
      ) : null}
    </ModalContainer>
  );
};
